import {
  Box,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { CustomAvatarGroup, CustomAvatar } from '../custom-avatar';
import { StatusTag } from '../status-tag';
import { Scrollbar } from '../scrollbar';
import { Strings } from '@app/constants';
import { IAddProjectSchema } from '@app/modules/projects';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { IUser, IManager } from '@app/modules/dashboard';
import { Card404 } from '../page-404';

interface Props {
  data?: any;
}

const ProjectSummaryCard = ({ data }: Props) => {
  const navigate = useNavigate();
  const currentDate = new Date();

  return (
    <Card
      sx={{
        ml: { xs: 1, md: 2 },
        mr: { xs: 1, md: 2 },
        borderRadius: '5px',
        mb: 2,
        width: { md: '97%', sm: '98%' },
        height: 350,
        overflow: 'hidden',
      }}
    >
      <Stack fontSize={17} fontWeight={700} ml={2} mt={2}>
        {Strings.dashboard.projectSummary}
      </Stack>
      <Scrollbar sx={{ height: 280 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{Strings.field.projectName}</TableCell>
              <TableCell align="center">{Strings.clients.clientName}</TableCell>
              <TableCell align="center">{Strings.project.teamMembers}</TableCell>
              <TableCell align="center">{Strings.projects.managers}</TableCell>
              <TableCell align="center">{Strings.projects.assignedOn}</TableCell>
              <TableCell align="center">{Strings.project.deadLine}</TableCell>
              <TableCell align="center">{Strings.field.status}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.projects && data?.projects?.length > 0 ? (
              data?.projects?.map((row: IAddProjectSchema, index: number) => {
                const endDate = new Date(row.endDate.split('/').reverse().join('-'));
                const isOverdue = endDate < currentDate;
                const openDetailsPage = (id: any) => {
                  navigate(`${PATH_DASHBOARD.projectDetail}/${row.name}`, {
                    state: { id: row.id },
                  });
                };
                return (
                  <TableRow key={index}>
                    <TableCell
                      align="center"
                      onClick={openDetailsPage}
                      sx={{ position: 'relative', overflow: 'hidden' }}
                    >
                      {isOverdue && (
                        <Box
                          sx={{
                            position: 'relative',
                            bottom: { xs: 0, sm: 1, md: 1, lg: 20, xl: 50 },
                            left: { xs: -38, sm: -48, md: -48, lg: -48, xl: -80 },
                            transform: 'rotate(-45deg)',
                          }}
                        >
                          <Chip
                            label="OVERDUE"
                            color="error"
                            size="small"
                            sx={{
                              height: { xs: 13, sm: 13, md: 13, lg: 13, xl: 15 },
                              width: { xs: 100, sm: 100, md: 100, lg: 107, xl: 150 },
                              fontSize: { xs: 7, sm: 7, md: 7, lg: 7, xl: 9 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              overflow: 'hidden',
                            }}
                          />
                        </Box>
                      )}
                      {row.name}
                    </TableCell>
                    <TableCell align="center" onClick={openDetailsPage}>
                      {row.clients?.firstName}
                    </TableCell>
                    <TableCell align="center" onClick={openDetailsPage}>
                      <CustomAvatarGroup max={3}>
                        {row.members?.map((memberId: any, idx: number) => {
                          const user = data?.users?.find((user: any) => user.id === memberId);
                          const userName = `${user?.firstName as string} ${
                            user?.lastName as string
                          }`;
                          return <CustomAvatar key={idx} name={userName} color="info" />;
                        })}
                      </CustomAvatarGroup>
                    </TableCell>
                    <TableCell align="center" onClick={openDetailsPage}>
                      <CustomAvatarGroup max={3}>
                        {row.managers?.map((memberId: IManager, idx: number) => {
                          const user = data?.users?.find(
                            (user: IUser) => user.id === Number(memberId)
                          );
                          const userName = `${user?.firstName as string} ${
                            user?.lastName as string
                          }`;
                          return <CustomAvatar key={idx} name={userName} color="info" />;
                        })}
                      </CustomAvatarGroup>
                    </TableCell>
                    <TableCell align="center" onClick={openDetailsPage}>
                      {row.startDate}
                    </TableCell>
                    <TableCell align="center" onClick={openDetailsPage}>
                      {row.endDate}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ borderBottom: 'none' }}
                      onClick={openDetailsPage}
                    >
                      <StatusTag status={row.status} />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow sx={{ '& td, & th': { borderBottom: 'none' } }}>
                <TableCell colSpan={8} sx={{ borderBottom: 'none' }}>
                  <Card404 height={150} message="Data Not Found!" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  );
};

export { ProjectSummaryCard };
